import React, { useState, useEffect } from 'react';
import { Lock, Check } from 'lucide-react';
import styles from './form.module.css';

const ImageChoiceForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const options = content?.options || [];
    const allowMultiple = content?.settings?.allowMultiple;

    const initSelected = () => {
        if (!userAnswer?.selectedOptions) return allowMultiple ? new Set() : null;
        return allowMultiple ? new Set(userAnswer.selectedOptions) : (userAnswer.selectedOptions[0] ?? null);
    };
    const [selected, setSelected] = useState(initSelected);

    useEffect(() => {
        if (!userAnswer?.selectedOptions) {
            setSelected(allowMultiple ? new Set() : null);
            return;
        }
        setSelected(allowMultiple ? new Set(userAnswer.selectedOptions) : (userAnswer.selectedOptions[0] ?? null));
    }, [userAnswer, allowMultiple]);

    const toggle = (id) => {
        if (optionsDisabled) return;
        if (allowMultiple) {
            setSelected(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id); else next.add(id);
                return next;
            });
        } else {
            setSelected(id);
        }
    };

    const isSelected = (id) => allowMultiple ? selected.has(id) : selected === id;
    const hasSubmitted = userAnswer != null;
    const optionsDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const canSubmit = allowMultiple ? selected.size > 0 : selected !== null;
    const currentArr = allowMultiple ? [...selected] : (selected !== null ? [selected] : []);
    const prevArr = userAnswer?.selectedOptions || [];
    const isUnchanged = hasSubmitted && JSON.stringify([...currentArr].sort()) === JSON.stringify([...prevArr].sort());
    const submitDisabled = isPreview || isLocked || !canSubmit || (hasSubmitted && (!allowUpdateResponse || isUnchanged));
    const submitLabel = !hasSubmitted ? (isLocked ? 'Voting Closed' : 'Submit') : (!allowUpdateResponse || isUnchanged ? <>Submitted <Check size={14} strokeWidth={3} aria-hidden /></> : 'Update response');

    const handleSubmit = () => {
        if (isLocked || submitDisabled) return;
        onSubmit({ selectedOptions: currentArr });
    };

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Choose an image'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden /> Voting Locked</span>}
            </div>
            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>{content.settings.descText}</div>
            )}
            <div className={styles.imageTileGrid}>
                {options.map((opt) => {
                    const selected = isSelected(opt.id);
                    return (
                        <button
                            key={opt.id}
                            type="button"
                            className={`${styles.imageTile} ${selected ? styles.selected : ''}`}
                            onClick={() => toggle(opt.id)}
                            disabled={optionsDisabled}
                            aria-pressed={selected}
                            aria-label={opt.label || `Option ${opt.id}`}
                        >
                            <img
                                src={opt.imageUrl}
                                alt={opt.label || `Option ${opt.id}`}
                                className={styles.imageTileImg}
                            />
                            {selected && (
                                <span className={styles.imageTileCheck} aria-hidden>
                                    <Check size={14} strokeWidth={3} />
                                </span>
                            )}
                            {opt.label && (
                                <div className={styles.imageTileLabel}>
                                    {opt.label}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitDisabled}>
                {submitLabel}
            </button>
        </div>
    );
};

export default ImageChoiceForm;
